import { useQuery } from 'react-query'
import { useSearchParams, Link } from 'react-router-dom'

import Button from 'components/Button'
import { postReportVoucherRedemptionCountService } from 'api/reports-voucher-redemption-count'
import { ExportToXLSX } from './utils-reports-xlsx-actions'

import { TRANSACTION_REPORT_CALENDAR_UNIT } from 'constants/filters'

type VoucherIssue = { voucherId: number; voucherName: string; voucherCode: string; issueCount: number }
type VoucherRedemptionItem = { unit: string; voucherId: number; redemptionCount: number }
type SearchQuery = { startDate: string; endDate: string; groupLevel: string }

const groupLevelToCalendarUnit = (groupLevel: number | string) => {
  switch (Number(groupLevel)) {
    case 1:
      return TRANSACTION_REPORT_CALENDAR_UNIT[0]
    case 2:
      return TRANSACTION_REPORT_CALENDAR_UNIT[1]
    case 3:
      return TRANSACTION_REPORT_CALENDAR_UNIT[2]
    case 4:
      return TRANSACTION_REPORT_CALENDAR_UNIT[3]
    default:
      return TRANSACTION_REPORT_CALENDAR_UNIT[0]
  }
}

const PieChart = ({
  data,
  size = 160,
  centerText,
}: {
  data: { label: string; value: number; color?: string }[]
  size?: number
  centerText?: string
}) => {
  const total = data.reduce((s, d) => s + d.value, 0)
  if (total === 0) return null
  let cumulative = 0
  const radius = Math.max(40, Math.floor(size / 2) - 8)
  const cx = Math.floor(size / 2)
  const cy = Math.floor(size / 2)

  const sectorPath = (start: number, end: number) => {
    const startRad = (start / total) * Math.PI * 2 - Math.PI / 2
    const endRad = (end / total) * Math.PI * 2 - Math.PI / 2
    const x1 = cx + radius * Math.cos(startRad)
    const y1 = cy + radius * Math.sin(startRad)
    const x2 = cx + radius * Math.cos(endRad)
    const y2 = cy + radius * Math.sin(endRad)
    const large = end - start > total / 2 ? 1 : 0
    return `M ${cx} ${cy} L ${x1} ${y1} A ${radius} ${radius} 0 ${large} 1 ${x2} ${y2} Z`
  }

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      {data.length === 1 ? (
        (() => {
          const d = data[0]
          const color = d.color || `hsl(200 70% 50%)`
          return <circle cx={cx} cy={cy} r={radius} fill={color} stroke="#fff" />
        })()
      ) : (
        data.map((d) => {
          const start = cumulative
          cumulative += d.value
          const end = cumulative
          const path = sectorPath(start, end)
          const color = d.color || `hsl(200 70% 50%)`
          return <path key={d.label} d={path} fill={color} stroke="#fff" />
        })
      )}
      {centerText && (
        <text x={cx} y={cy} textAnchor="middle" dominantBaseline="central" fontSize={12} fill="#111">
          {centerText}
        </text>
      )}
    </svg>
  )
}

export const ViewVoucherRedemption = () => {
  const [searchParams] = useSearchParams()
  const searchQuery = JSON.parse(decodeURIComponent(searchParams.get('report-query')!))
  const searchRows: SearchQuery[] = Array.isArray(searchQuery) ? searchQuery : [searchQuery]
  const { data, isLoading, isError } = useQuery(['voucherRedemptionReport', searchQuery], () => postReportVoucherRedemptionCountService(searchQuery))

  if (isLoading) return <div>Loading...</div>
  if (isError) return <div>Error loading report</div>

  const apiData = data?.data

  const issueCounts = apiData?.voucherIssueCounts || []
  const redemptionCounts = apiData?.voucherRedemptionCounts || []

  const redemptionByVoucherAndUnit: Record<number, Record<string, number>> =
    redemptionCounts.reduce((acc, item) => {
      if (!acc[item.voucherId]) acc[item.voucherId] = {}

      const unit = item.unit || 'N/A'
      acc[item.voucherId][unit] =
        (acc[item.voucherId][unit] || 0) + item.redemptionCount

      return acc
    }, {} as Record<number, Record<string, number>>)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const voucherCodeById: Record<number, string> = issueCounts.reduce((acc, it: any) => {
    acc[it.voucherId] = it.voucherCode || acc[it.voucherId] || 'N/A'
    return acc
  }, {} as Record<number, string>)
  const voucherName = issueCounts.length > 0 ? issueCounts[0].voucherName : 'N/A'
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const voucherNameById: Record<number, string> = issueCounts.reduce((acc, it: any) => {
    acc[it.voucherId] = it.voucherName || acc[it.voucherId] || 'N/A'
    return acc
  }, {} as Record<number, string>)

  const redemptionByVoucher = redemptionCounts.reduce<Record<number, number>>((acc, item: VoucherRedemptionItem) => {
    acc[item.voucherId] = (acc[item.voucherId] || 0) + item.redemptionCount
    return acc
  }, {})

  const issueByVoucher = issueCounts.reduce<Record<number, number>>((acc, item: VoucherIssue) => {
    acc[item.voucherId] = (acc[item.voucherId] || 0) + item.issueCount
    return acc
  }, {})

  const combinedVoucherIds = Array.from(new Set([...Object.keys(issueByVoucher).map(Number), ...Object.keys(redemptionByVoucher).map(Number)]))
  const sortedVoucherIds = combinedVoucherIds.sort((a, b) => a - b)
  const label = groupLevelToCalendarUnit(Number(searchRows[0]?.groupLevel)).label;

  const xlsxRows = sortedVoucherIds.flatMap((vid) => {
    const units = Object.entries(redemptionByVoucherAndUnit[vid] || {});

    if (!units.length) {
      return [{
        voucherId: vid,
        voucherCode: voucherCodeById[vid] || 'N/A',
        voucherName: voucherNameById[vid] || voucherName,
        totalIssued: issueByVoucher[vid] || 0,
        [label]: 'N/A',
        redeemed: 0,
      }];
    }

    return units.map(([unit, count], index) => ({
      voucherId: index === 0 ? vid : '',
      voucherCode: index === 0 ? voucherCodeById[vid] || 'N/A' : '',
      voucherName: index === 0 ? voucherNameById[vid] || voucherName : '',
      totalIssued: index === 0 ? issueByVoucher[vid] || 0 : '',
      [label]: unit,
      redeemed: count,
    }));
  });

  return (
    <div className="my-6">
      <div className="flex items-center gap-4">
        <button className="px-3 py-1 border rounded-md border-primary text-primary" type="button">
          <Link to={`/reports-list?modal=voucher_redemption`} className="flex items-center gap-2">
            <span className="tracking-wider font-light">Back to reports</span>
          </Link>
        </button>

        <Button
          type="button"
          className="ml-1 rounded-lg tracking-wide font-bold"
          iconRight={false}
          onClick={() =>
            ExportToXLSX(xlsxRows, {
              filename: 'Voucher-Issues-vs-Redemptions.xlsx',
            })
          }
        >
          Download XLSX Report
        </Button>
      </div>

      <br />

      <div className="flex gap-10 items-start">
        <div>
          <h4 className="mb-1 font-bold">Criteria Used</h4>
          <table className="table-auto border-collapse">
            <thead>
              <tr>
                <th className="px-2 pl-0">Start Date</th>
                <th className="px-2">End Date</th>
                <th className="px-2">Group By</th>
              </tr>
            </thead>
            <tbody>
              {searchRows.map((row: SearchQuery) => (
                <tr key={row.startDate + row.endDate}>
                  <td className="px-2 pl-0">{row.startDate.slice(0, 10)}</td>
                  <td className="px-2">{row.endDate.slice(0, 10)}</td>
                  <td className="px-2">{groupLevelToCalendarUnit(Number(row.groupLevel)).label}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex gap-10 items-start mt-6">
        <div>
          {sortedVoucherIds.length ? (
            <table className="table-auto border-collapse">
              <tbody>
                {sortedVoucherIds.map((id) => {
                  const unitEntries = Object.entries(redemptionByVoucherAndUnit[id] || {})

                  const sortedUnits = unitEntries.sort(([a], [b]) => {
                    const parse = (d: string) => {
                      const [day, month, year] = d.split('-').map(Number)
                      return new Date(year, month - 1, day).getTime()
                    }
                    return parse(a) - parse(b)
                  })
                  return (
                    <div key={id} className="mb-6 border p-4 rounded-md bg-gray-50">
                      <div className="mb-2 font-semibold">
                        Voucher: {voucherCodeById[id] || 'N/A'} – {voucherNameById[id] || voucherName}
                      </div>
                      <div className="mb-4 text-sm text-gray-700">
                        Total Issued: {issueByVoucher[id] || 0} | Total Redeemed: {redemptionByVoucher[id] || 0}
                      </div>

                      <table className="table-auto border-collapse w-full text-left">
                        <thead>
                          <tr className="border-b">
                            <th className="px-2 py-1">{groupLevelToCalendarUnit(Number(searchRows[0]?.groupLevel)).label}</th>
                            <th className="px-2 py-1">Redeemed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedUnits.map(([unit, count]) => (
                            <tr key={`${id}-${unit}`} className="border-b">
                              <td className="px-2 py-1">{unit}</td>
                              <td className="px-2 py-1">{count}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                })}
              </tbody>
            </table>
          ) : (
            <p>No redemption data available</p>
          )}
        </div>
      </div>

      <div className="mt-2">
        <h4 className="font-bold">Per Voucher Charts</h4>
        {combinedVoucherIds.length === 0 ? (
          <p className="mt-2">No voucher data available</p>
        ) : (
          <div className="flex gap-6 flex-wrap">
            {combinedVoucherIds.slice(0, 5).map((vid) => {
              const issued = issueByVoucher[vid] || 0
              const redeemed = redemptionByVoucher[vid] || 0
              const chartData = [] as { label: string; value: number; color?: string }[]
              // issued = red, redeemed = blue
              if (issued > 0) chartData.push({ label: 'Issued', value: issued, color: '#e53e3e' })
              if (redeemed > 0) chartData.push({ label: 'Redeemed', value: redeemed, color: '#3182ce' })

              return (
                <div key={vid} className="w-44 bg-white p-2 rounded shadow-sm">
                  <div className="flex justify-center">
                    {chartData.length ? (
                      <PieChart data={chartData} size={140} />
                    ) : (
                      <div className="text-center text-sm text-gray-600">No data</div>
                    )}
                  </div>
                  <div className="mt-2 text-center text-sm font-bold">{voucherCodeById[vid] || 'N/A'}</div>
                  <div className="mt-3 flex justify-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <span style={{ width: 12, height: 12, background: '#e53e3e', display: 'inline-block' }} />
                      <span>Issued</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span style={{ width: 12, height: 12, background: '#3182ce', display: 'inline-block' }} />
                      <span>Redeemed</span>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

export default ViewVoucherRedemption
